// react
import { useEffect, useCallback, useRef } from 'react';

// api
import { useSelector } from 'store';
import { getChatToken, setChatToken } from 'store/slices/chatSlice';
import { getAccount } from 'store/slices/iamSlice';
import { useGenerateChatTokenMutation } from 'store/api/chat';

// hooks
import { useAuth } from 'hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';

// intercom
import { Intercom } from '@intercom/messenger-js-sdk';
import { Intercom as CapacitorIntercom } from '@capacitor-community/intercom';
import { PushNotifications, Token } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

const INTERCOM_APP_ID = import.meta.env.REACT_APP_INTERCOM_APP_ID || '';

const useIntercom = () => {
  // state
  const account = useSelector(getAccount);
  const chatToken = useSelector(getChatToken);
  const isIntercomReady = useRef(false);
  const isLoading = useRef(false);

  // hooks
  const [generateChatToken] = useGenerateChatTokenMutation();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const flags = useFlags();

  // methods
  const shutdown = useCallback(() => {
    setChatToken(null);
    isIntercomReady.current = false;
    isLoading.current = false;
    window.Intercom?.('shutdown');
  }, []);

  const showIntercom = useCallback((): void => {
    if (Capacitor.isNativePlatform()) {
      CapacitorIntercom.displayMessenger();
    } else {
      window.Intercom?.('show');
    }
  }, []);

  // checks whether intercom has fully loaded yet
  const pollIntercomReady = useCallback(() => {
    if (isIntercomReady.current) return;

    const intervalID = setInterval(() => {
      if (isIntercomReady.current) {
        clearInterval(intervalID);
        return;
      }

      if (
        window.Intercom &&
        !window.Intercom.toString().includes('queueHolder.loaderQueue')
      ) {
        clearInterval(intervalID);
        isLoading.current = false;
        isIntercomReady.current = true;
      }
    }, 50);
  }, []);

  const mountCapacitorIntercom = useCallback(async () => {
    if (!chatToken || !chatToken.userID || !chatToken.email) {
      console.log('no chat token', chatToken);
      return;
    }

    await PushNotifications.addListener(
      'registration',
      async (token: Token) => {
        await CapacitorIntercom.sendPushTokenToIntercom({ value: token.value });
      },
    );

    console.log('registering identified user', chatToken);

    await CapacitorIntercom.registerIdentifiedUser({
      userId: chatToken?.userID,
      email: chatToken?.email,
    });
  }, [chatToken]);

  // mounts intercom and starts polling for whether it has mounted
  const mountIntercom = useCallback(async () => {
    if (isIntercomReady.current || isLoading.current) return;
    isLoading.current = true;

    if (!chatToken) {
      isLoading.current = false;
      return;
    }

    if (Capacitor.isNativePlatform()) {
      console.log('mounting capacitor intercom');
      mountCapacitorIntercom();
      return;
    }

    Intercom({
      app_id: INTERCOM_APP_ID,
      region: 'eu',
      user_id: chatToken?.userID,
      name: chatToken?.name,
      email: chatToken?.email,
      created_at: chatToken?.createdAt,
      intercom_user_jwt: chatToken?.token,
      session_duration: 86400000,
      hide_default_launcher: true,
    });

    pollIntercomReady();

    return;
  }, [chatToken, pollIntercomReady]);

  // effects
  // checks whether to show intercom UI once it's loaded
  useEffect(() => {
    if (isLoading.current || !isIntercomReady.current) return;

    const currentPath = window.location.pathname;
    const messengerParam = new URLSearchParams(window.location.search).get(
      'messenger',
    );
    if (currentPath === '/messenger' || messengerParam === 'true') {
      showIntercom();
    }
  }, [showIntercom]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !isAuthLoading &&
      account &&
      account.id &&
      account.accountRole?.name !== 'KareHero Administrator' &&
      account.accountRole?.name !== 'Organization Owner' &&
      flags?.intercomChat &&
      !isLoading.current &&
      !chatToken
    ) {
      generateChatToken();
    }
  }, [
    chatToken,
    generateChatToken,
    isAuthenticated,
    isAuthLoading,
    account,
    flags?.intercomChat,
  ]);

  return {
    showIntercom,
    mountIntercom,
    shutdown,
    isLoading: isLoading.current,
    isIntercomReady: isIntercomReady.current,
  };
};

export default useIntercom;
